import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { profileService } from '../../services/api'

const initialState = {
  profile: null,
  stats: null,
  loading: false,
  error: null,
  uploadingAvatar: false,
  changingPassword: false,
  avatarError: null,
  passwordError: null
}

// Async thunks
export const fetchProfile = createAsyncThunk(
  'profile/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileService.getProfile()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Falha ao carregar perfil')
    }
  }
)

export const updateProfile = createAsyncThunk(
  'profile/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await profileService.updateProfile(profileData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Falha ao atualizar perfil')
    }
  }
)

export const uploadAvatar = createAsyncThunk(
  'profile/uploadAvatar',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData()
      formData.append('avatar', file)
      
      const response = await profileService.uploadAvatar(formData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Falha ao fazer upload do avatar')
    }
  }
)

export const removeAvatar = createAsyncThunk(
  'profile/removeAvatar',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileService.removeAvatar()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Falha ao remover avatar')
    }
  }
)

export const changePassword = createAsyncThunk(
  'profile/changePassword',
  async (passwordData, { rejectWithValue }) => {
    try {
      const response = await profileService.changePassword(passwordData)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Falha ao alterar senha')
    }
  }
)

export const fetchProfileStats = createAsyncThunk(
  'profile/fetchProfileStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileService.getProfileStats()
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Falha ao carregar estatísticas')
    }
  }
)

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
      state.avatarError = null
      state.passwordError = null
    },
    clearProfile: (state) => {
      state.profile = null
      state.stats = null
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload
        state.error = null
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Update Profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = { ...state.profile, ...action.payload }
        state.error = null
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      
      // Upload Avatar
      .addCase(uploadAvatar.pending, (state) => {
        state.uploadingAvatar = true
        state.avatarError = null
      })
      .addCase(uploadAvatar.fulfilled, (state, action) => {
        state.uploadingAvatar = false
        if (state.profile && state.profile.dataGeneral) {
          state.profile.dataGeneral.avatar = action.payload.avatar
        }
        state.avatarError = null
      })
      .addCase(uploadAvatar.rejected, (state, action) => {
        state.uploadingAvatar = false
        state.avatarError = action.payload
      })
      
      // Remove Avatar
      .addCase(removeAvatar.pending, (state) => {
        state.uploadingAvatar = true
        state.avatarError = null
      })
      .addCase(removeAvatar.fulfilled, (state) => {
        state.uploadingAvatar = false
        if (state.profile && state.profile.dataGeneral) {
          state.profile.dataGeneral.avatar = null
        }
        state.avatarError = null
      })
      .addCase(removeAvatar.rejected, (state, action) => {
        state.uploadingAvatar = false
        state.avatarError = action.payload
      })
      
      // Change Password
      .addCase(changePassword.pending, (state) => {
        state.changingPassword = true
        state.passwordError = null
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.changingPassword = false
        state.passwordError = null
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.changingPassword = false
        state.passwordError = action.payload
      })
      
      // Fetch Profile Stats
      .addCase(fetchProfileStats.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchProfileStats.fulfilled, (state, action) => {
        state.loading = false
        state.stats = action.payload
      })
      .addCase(fetchProfileStats.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  }
})

export const { clearError, clearProfile } = profileSlice.actions

export default profileSlice.reducer